import React, { useCallback, useEffect, useId, useRef } from 'react';
import { WarningOutlined } from '@ant-design/icons';
import styled, { css } from 'styled-components';

import { screenFilterSummary } from 'containers/Intelligence/filters/screenFilterSignature';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';
import usePerx3Redesign from 'hooks/usePerx3Redesign';

import { useOrbitContext } from '../OrbitContext';
import {
  describeOrbitContext,
  describeOrbitFilterChip,
  describeOrbitSubject
} from '../contextLabel';
import OrbitGlyphText from '../glyphIcons';
import { TOrbit } from '../i18n/TModules';
import {
  OrbitChip,
  OrbitChipRow,
  orbitTokens,
  orbitType,
  perx3OrbitPrimaryButton
} from '../styles';
import OrbitCardChips from './OrbitCardChips';
import OrbitChipIcon from './OrbitChipIcon';

/**
 * Both paints are `#F3B95F`; what separates them is the opacity the earlier Properties-panel read
 * dropped. The scene graph for `5032:9696` gives fill opacity 0.14 and stroke opacity 0.4, so the
 * amber is a wash over the thread surface, not a solid ground. On the composite the title clears
 * 7.5:1 and the body 11.5:1.
 */
const CARD_FILL = orbitTokens.conflictFill;
const CARD_STROKE = orbitTokens.conflictStroke;

const Card = styled.div`
  width: 100%;
  padding: 13px 14px;
  background: ${CARD_FILL};
  border: 1px solid ${CARD_STROKE};
  border-radius: 12px;
  box-shadow: 0 4px 4px ${orbitTokens.shadow};

  &:focus {
    outline: none;
  }

  /*
    Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). The ampersand is
    doubled so the rule outranks the zero margin the children set: at equal specificity source order
    decided it, and the title and body ended up flush against each other.
  */
  && > * + * {
    margin-top: 7px;
  }
`;

// Title and body sit one step above 5032:9595 (10.5 and 9.7) on purpose: the user asked on
// 2026-09-29 for a larger title and a larger, more open body sentence.
const Title = styled.h3`
  margin: 0;
  padding-bottom: 1px;
  color: ${orbitTokens.warning};
  ${orbitType.conflictTitle}
  font-weight: 700;

  > .anticon {
    margin-right: 6px;
  }
`;

const Body = styled.p`
  margin: 0;
  color: ${orbitTokens.threadText};
  ${orbitType.conflictBody}
  font-weight: 400;
`;

const Compare = styled.div`
  padding: 10px 10px 8px;
  background: ${orbitTokens.surfaceAlt};
  border-radius: 8px;

  > * + * {
    margin-top: 5px;
  }
`;

const CompareRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  ${orbitType.conflictCompare}

  > * + * {
    margin-left: 12px;
  }
`;

const CompareLabel = styled.span`
  flex: 0 0 auto;
  color: ${orbitTokens.threadFaint};
  font-weight: 400;
`;

/*
  `5032:9707` and `5032:9712` are auto-width single-line boxes 11 tall. Left to wrap, a runtime
  value two or three times the drawn length breaks into a ragged right-aligned stack and the row
  grows to 22; one line that ellipsises keeps the drawn rhythm, and `title` recovers the full text.
*/
const CompareValue = styled.span`
  min-width: 0;
  overflow: hidden;
  color: ${orbitTokens.threadMuted};
  font-weight: 400;
  text-align: right;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// `5032:9707` is STRIKETHROUGH: the struck line is what marks the context as the superseded one.
const CompareOldValue = styled(CompareValue)`
  text-decoration: line-through;
`;

const CompareNewValue = styled(CompareValue)`
  color: ${orbitTokens.compareNewInk};
  font-weight: 700;
`;

// Doubled ampersand for the same reason the card needs one: the buttons reset their own margin at
// equal specificity, and the 6px the frame puts between them was being dropped.
const Actions = styled.div`
  padding-top: 4px;

  && > * + * {
    margin-top: 6px;
  }
`;

/**
 * §4c: the frame renders these in Arial. That is a font-fallback accident in the import, so they
 * inherit the panel's Inter stack instead.
 *
 * The fill is the drawn `#4F46E5` (`5032:9715`, `5032:9718`, `5032:9720`), which §2 records as the
 * change to adopt: `#FFFFFF` on it is 6.29:1, against 3.29:1 on the `#3E8FF0` this shipped with.
 * `#EAF0F7` on it, the ink `5032:9719` draws, is 5.48:1.
 */
const CONFLICT_BUTTON_FILL = orbitTokens.actionStrong;

const buttonRules = css`
  display: block;
  box-sizing: border-box;
  width: 100%;
  margin: 0;
  padding: 9px 11px;
  background: ${CONFLICT_BUTTON_FILL};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-family: inherit;
  ${orbitType.action}
  font-weight: 700;
  text-align: left;

  &:hover {
    filter: brightness(1.14);
  }

  &:focus-visible {
    outline: 2px solid ${orbitTokens.text};
    outline-offset: 1px;
  }
`;

/**
 * All three are drawn as the same solid `#4F46E5`; the frame's own hierarchy is the stroke, which
 * only `5032:9715` carries and which is what makes it 33 tall against the other two at 31. The inks
 * stay as drawn: `#FFFFFF`, then `#EAF0F7` on `5032:9719`, then `#FFFFFF`.
 */
/*
  PERX3-REDESIGN round 7: all three are solid-fill primaries, so they take the redesign's black
  treatment on a `$perx3` branch layered over the drawn rules — the OFF render is the fill and the
  three inks above, untouched. The brightness hover goes with it: it lifted the drawn indigo, and on
  a black fill the black hover is what the treatment states.
*/
const perx3ConflictButton = css`
  ${perx3OrbitPrimaryButton}

  &:hover {
    filter: none;
  }
`;

const PrimaryButton = styled.button<{ $perx3: boolean }>`
  ${buttonRules}
  border: 1px solid ${CONFLICT_BUTTON_FILL};
  color: ${orbitTokens.onAction};

  ${({ $perx3 }) => $perx3 && perx3ConflictButton}
`;

const SecondaryButton = styled.button<{ $perx3: boolean }>`
  ${buttonRules}
  color: ${orbitTokens.threadText};

  ${({ $perx3 }) => $perx3 && perx3ConflictButton}
`;

const TertiaryButton = styled.button<{ $perx3: boolean }>`
  ${buttonRules}
  color: ${orbitTokens.onAction};

  ${({ $perx3 }) => $perx3 && perx3ConflictButton}
`;

/*
  The pill row the frame draws inside the card (`5032:9722` Period, `5032:9724` Region). Period is
  read from the live Customer Insights filter panel. `flow-root` keeps the shared row's -8px
  wrap-compensation margin inside this block; escaping, it ate the 7px gap to the buttons.
*/
const Pills = styled.div`
  display: flow-root;
  overflow: hidden;
`;

/*
  Every chip carries an 8px right margin, so the last chip on a line needs 8px it never uses and
  wrapped a chip that fits onto a line of its own. The row takes that 8px back; Pills clips it.
*/
const ChipRow = styled(OrbitChipRow)`
  margin-right: -8px;

  > * {
    max-width: calc(100% - 8px);
  }
`;

/*
  TODO(AF-10): the Region pill (`5032:8071`) has no source. Neither screen has a region category —
  `IntelligenceFilterId` is an exhaustive eight-member union with none, `Customer360FilterId` a
  three-member one — so a pill reading "All" would assert something nothing has checked, and it
  stays out until one exists.
*/

/**
 * div#conflictCard `4952:7658`, floated over the panel body on `OrbitSidebar`'s scrim
 * (`5032:7990`). Raised when the route moved away from the context the running investigation was
 * started in; the three buttons are the only way to resolve it.
 *
 * `alertdialog` with no `aria-modal`: the card interrupts the PANEL, and the panel's scrim stops at
 * the panel's own edge. The page beside it stays live and reachable by Tab, so claiming a modal
 * would misreport the rest of the document as inert. What is actually inert — the transcript and
 * the composer under the scrim — `OrbitSidebar` marks as such.
 *
 * Escape is deliberately NOT handled here. None of the three choices is a safe default, so there is
 * nothing for a dismissal to mean; the key is left to bubble to the panel, which closes. That
 * discards no decision: the conflict is still unresolved and the card is raised again the moment
 * the panel reopens.
 */
const OrbitConflictCard: React.FC = () => {
  const { t } = useMultiLanguageTranslation();
  const {
    entryContext,
    entryFilters,
    sessionContext,
    sessionFilters,
    turns,
    adoptLiveContext,
    pinSessionContext,
    newChat
  } = useOrbitContext();
  const perx3 = usePerx3Redesign();
  const cardRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const bodyId = useId();

  // Focus moves to the card, not to one of its buttons: pre-selecting a choice on a decision the
  // user has not read is worse than a container a screen reader announces title-then-body from.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;

    cardRef.current?.focus({ preventScroll: true });

    return () => {
      if (opener !== null && opener.isConnected) {
        opener.focus?.({ preventScroll: true });
      }
    };
  }, []);

  const handleStartNew = useCallback(() => newChat(), [newChat]);

  // The compare row names the filters too: on Customer Insights they are what moved, and without
  // them the two rows read identically after a filter-only change (PLA-8844 D2).
  const withFilters = (value: string, filters: string): string =>
    filters === '' ? value : t(`${TOrbit}.conflict.withFilters`, { value, filters });
  const liveFilters = screenFilterSummary(entryFilters);
  const liveCards = entryFilters?.cards ?? [];
  const oldValue = withFilters(describeOrbitSubject(t, turns, sessionContext), sessionFilters);
  const newValue = withFilters(describeOrbitContext(t, entryContext), liveFilters);

  return (
    <Card
      ref={cardRef}
      role="alertdialog"
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      tabIndex={-1}
      data-testid="orbit-conflict-card"
    >
      <Title id={titleId}>
        <WarningOutlined aria-hidden="true" data-testid="orbit-conflict-icon" />
        <OrbitGlyphText text={t(`${TOrbit}.conflict.title`)} />
      </Title>
      <Body id={bodyId}>{t(`${TOrbit}.conflict.body`)}</Body>
      <Compare data-testid="orbit-conflict-compare">
        <CompareRow>
          <CompareLabel>{t(`${TOrbit}.conflict.investigating`)}</CompareLabel>
          <CompareOldValue data-testid="orbit-conflict-old" title={oldValue}>
            {oldValue}
          </CompareOldValue>
        </CompareRow>
        <CompareRow>
          <CompareLabel>{t(`${TOrbit}.conflict.nowShows`)}</CompareLabel>
          <CompareNewValue data-testid="orbit-conflict-new" title={newValue}>
            {newValue}
          </CompareNewValue>
        </CompareRow>
      </Compare>
      {entryFilters !== null && (entryFilters.chips.length > 0 || liveCards.length > 0) ? (
        <Pills>
          {/* The same chip row the context section draws, so the card names every filter the
              dashboard has moved to and not the date range alone, and every card the page now has
              on screen (PLA-8844). Every chip goes through `OrbitGlyphText` for the reason the
              context section's do: the copy opens on the frame's own `📊` / `📅`, which under the
              flag is drawn as the matching antd icon and with it off is the string verbatim
              (PERX3-REDESIGN round 7); the card chips carry it inside `OrbitCardChips`. */}
          <ChipRow data-testid="orbit-conflict-chips">
            <OrbitCardChips cards={liveCards} testIdPrefix="orbit-conflict" />
            {entryFilters.chips.map(chip => (
              <OrbitChip key={chip.id} data-testid={`orbit-conflict-chip-${chip.id}`}>
                <OrbitChipIcon kind={chip.id} />
                <OrbitGlyphText text={describeOrbitFilterChip(t, chip)} />
              </OrbitChip>
            ))}
          </ChipRow>
        </Pills>
      ) : null}
      <Actions>
        <PrimaryButton
          type="button"
          $perx3={perx3}
          data-testid="orbit-conflict-adopt"
          onClick={adoptLiveContext}
        >
          {t(`${TOrbit}.conflict.continueNew`)}
        </PrimaryButton>
        <SecondaryButton
          type="button"
          $perx3={perx3}
          data-testid="orbit-conflict-new-chat"
          onClick={handleStartNew}
        >
          {t(`${TOrbit}.conflict.saveAndStart`)}
        </SecondaryButton>
        <TertiaryButton
          type="button"
          $perx3={perx3}
          data-testid="orbit-conflict-keep"
          onClick={pinSessionContext}
        >
          {t(`${TOrbit}.conflict.keepCurrent`)}
        </TertiaryButton>
      </Actions>
    </Card>
  );
};

export default OrbitConflictCard;
